/**
 * Module-level queue that hands File[] captured on an UploadJourney tab dropzone
 * (click or drop) over to the Magic Upload drawer's MagicUploadCenter, which lives
 * in a sibling component tree. Independent of mount order — a drop can enqueue
 * before the drawer mounts, and a click can mount the drawer before the OS picker
 * resolves; either way the consumer drains on the next version bump.
 *
 * File objects are intentionally kept OUT of redux (not serialisable) and out of
 * any URL/localStorage — see RECUR-28067 plan D3.
 */
export type PendingUpload = {
    files: File[];
    uploadTab?: string;
    /** Untagged-fallback docType applied only if Pandora leaves the file untagged (RECUR-28067 v4). */
    fallbackDocType?: string;
};

let queue: PendingUpload[] = [];
let version = 0;
const subscribers = new Set<() => void>();

const notify = () => {
    version += 1;
    subscribers.forEach((listener) => listener());
};

export const enqueuePendingUpload = (entry: PendingUpload): void => {
    queue = [...queue, entry];
    notify();
};

/** Destructive read: returns every queued entry (FIFO) and empties the queue. */
export const drainPendingUploads = (): PendingUpload[] => {
    const drained = queue;
    queue = [];
    return drained;
};

export const clearPendingUploads = (): void => {
    queue = [];
};

export const subscribePendingUploads = (listener: () => void): (() => void) => {
    subscribers.add(listener);
    return () => {
        subscribers.delete(listener);
    };
};

export const getPendingUploadsVersion = (): number => version;
